import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useAuth } from "@/hooks/auth/useAuth";
import { useProfileMutation, useProfileQuery } from "@/queries/profile";
import { ArrowLeft, LoaderCircle } from "lucide-react";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Textarea } from "@/components/ui/textarea";

const profileSchema = z.object({
  full_name: z
    .string()
    .min(2, "Le nom doit contenir au moins 2 caractères")
    .max(50, "Le nom ne doit pas dépasser 50 caractères")
    .nonempty("Nom requis"),
  bio: z
    .string()
    .max(200, "La bio ne doit pas dépasser 200 caractères")
    .optional(),
});
type ProfileFormData = z.infer<typeof profileSchema>;

const Profile = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const userId = user?.id;

  const { data: profile, isLoading: isProfileLoading } =
    useProfileQuery(userId);
  const mutation = useProfileMutation(userId);

  const form = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: { full_name: "", bio: "" },
  });

  useEffect(() => {
    if (profile) {
      form.reset({ full_name: profile.full_name, bio: profile.bio ?? "" });
    }
  }, [profile, form]);

  const onSubmit = (data: ProfileFormData) => {
    mutation.mutate({ full_name: data.full_name, bio: data.bio });
  };

  return (
    <div className="gradient-bg flex min-h-full flex-col items-center justify-start px-4 py-6">
      <div className="mx-auto w-full max-w-lg">
        {/* Header with back button and title */}
        <div className="mb-6 flex items-center gap-4">
          <Button
            variant="ghost"
            className="p-2"
            onClick={() => navigate(-1)}
            aria-label="Retour"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-semibold text-gray-700">Profil</h1>
        </div>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-4 rounded-xl bg-white px-5 py-6 shadow-sm"
          >
            <FormField
              control={form.control}
              name="full_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nom</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Les personnages voient ce nom"
                      required
                      autoFocus
                      disabled={isProfileLoading || mutation.isPending}
                      className="mt-2"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* Bio field */}
            <FormField
              control={form.control}
              name="bio"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Bio</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="Quelques mots sur vous"
                      disabled={isProfileLoading || mutation.isPending}
                      className="mt-2 text-base"
                      rows={6}
                      maxLength={200}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type="submit"
              disabled={
                mutation.isPending ||
                isProfileLoading ||
                !form.formState.isDirty
              }
              className="w-full"
            >
              {mutation.isPending || isProfileLoading ?
                <LoaderCircle className="h-5 w-5 animate-spin" />
              : "Enregistrer"}
            </Button>
          </form>
        </Form>
      </div>
    </div>
  );
};

export default Profile;
